/**
 * VENDOR MODULES
 */
import React from 'react';

import BetsArea from 'features/markets/components/BetsArea';
import { useSchedulesQuery } from 'features/events/queries';
import { useSchedulePanelContext } from 'features/events/SchedulePanelContext';
import { useSelectedEventForSchedule } from 'features/events/hooks/useSelectedEventForSchedule';
import Football3dWeekArea from 'features/markets/components/bets-area/Football3dWeekArea';
import ScheduleAreaTwoTabs from 'features/markets/components/bets-area/ScheduleAreaTwoTabs';
import { useServerNow } from 'shared/queries/serverTime';
import { useEventResultsQuery } from 'features/results/queries';
import { getEventEndMsFromResults } from 'features/results/eventEndFromResults';
import { ClosedEventPanel } from 'features/results/components/ClosedEventPanel';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';

const RACING_SPORT_IDS = [10, 11, 20, 21, 30];

export function NonFootballBetsArea(props) {
  const { t } = useTranslation();
  const { event, isFootball3d } = props;

  const serverNowMs = useServerNow();
  const { event_id, sport_id } = event || {};
  const eventStartMs = event?.event_timestamp ? Number(event.event_timestamp) * 1000 : null;
  const hasStarted = eventStartMs != null && Number.isFinite(eventStartMs) && serverNowMs >= eventStartMs;

  const resultsQuery = useEventResultsQuery({
    eventId: event_id,
    scheduleId: event?.schedule_id,
    enabled: !!event_id && hasStarted && !isFootball3d,
  });
  const effectiveResults = resultsQuery.data;
  const hasResults = !!effectiveResults && Object.keys(effectiveResults || {}).length > 0;
  const eventEndMs = getEventEndMsFromResults(effectiveResults ?? null);
  const eventHasEnded =
    eventEndMs != null && Number.isFinite(eventEndMs) && serverNowMs >= eventEndMs;

  const showResultsPanel = hasResults && eventHasEnded;

  if (!event_id) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground" role="status">
        {t('NO_EVENTS') || 'No events'}
      </div>
    );
  }

  if (showResultsPanel) {
    return (
      <ClosedEventPanel
        eventId={event_id}
        sportId={event?.sport_id}
        scheduleId={event?.schedule_id}
        resultsOverride={effectiveResults}
        eventTimestamp={event?.event_timestamp}
      />
    );
  }

  return <BetsArea {...event} />;
}

/**
 * Panel content for one schedule. Must be rendered inside SchedulePanelProvider.
 * Uses context for scheduleId and useSelectedEventForSchedule for the panel's event.
 * For ended events we show only results (no Markets/Results tabs).
 */
function BetsAreaForSchedule() {
  const { t } = useTranslation();
  const ctx = useSchedulePanelContext();
  const { data: schedules = [] } = useSchedulesQuery();
  const event = useSelectedEventForSchedule(ctx?.scheduleId ?? null);
  const serverNowMs = useServerNow();
  const { event_id, sport_id, schedule_id } = event || {};
  const eventStartMs = event?.event_timestamp ? Number(event.event_timestamp) * 1000 : null;
  const hasStarted =
    !!event_id &&
    eventStartMs != null &&
    Number.isFinite(eventStartMs) &&
    serverNowMs >= eventStartMs;
  const eventResultsQuery = useEventResultsQuery({
    eventId: event_id,
    scheduleId: schedule_id ?? ctx?.scheduleId,
    enabled: !!event_id && hasStarted,
  });
  const effectiveResults = eventResultsQuery.data;
  const hasResults = !!effectiveResults && Object.keys(effectiveResults || {}).length > 0;
  const eventEndMs = getEventEndMsFromResults(effectiveResults ?? null);
  const eventHasEnded =
    eventEndMs != null && Number.isFinite(eventEndMs) && serverNowMs >= eventEndMs;
  const showOnlyResults = hasResults && eventHasEnded;
  const isEventRunning = hasStarted && !eventHasEnded;

  if (!ctx) return null;

  const scheduleId = ctx.scheduleId;
  const currentSchedule = Array.isArray(schedules)
    ? schedules.find((s) => s.schedule_id === scheduleId)
    : null;
  const currentSportIdRaw = currentSchedule?.sport?.sport_id ?? currentSchedule?.sport_id;
  const currentSportId =
    currentSportIdRaw != null ? parseInt(String(currentSportIdRaw), 10) : null;
  const isFootball3d = currentSportId === 50;
  const isBaseball = currentSportId === 40;
  const isRacing = currentSportId != null && RACING_SPORT_IDS.includes(currentSportId);

  const eventForPanel = event != null ? event : {};

  if (isFootball3d) {
    return <Football3dWeekArea />;
  }

  if (isBaseball || isRacing) {
    if (!event_id) {
      return <NonFootballBetsArea event={eventForPanel} isFootball3d={false} />;
    }
    if (showOnlyResults) {
      return <NonFootballBetsArea event={eventForPanel} isFootball3d={false} />;
    }
    if (isEventRunning) {
      return (
        <div className="p-3 border-b border-border bg-warning/20 text-warning-foreground text-sm font-semibold">
          {t('EVENT_RUNNING_BETTING_DISABLED') || 'Event is running. Betting is closed.'}
        </div>
      );
    }
    return (
      <ScheduleAreaTwoTabs sportId={currentSportId} event={eventForPanel}>
        <NonFootballBetsArea event={eventForPanel} isFootball3d={false} />
      </ScheduleAreaTwoTabs>
    );
  }

  return <NonFootballBetsArea event={eventForPanel} isFootball3d={false} />;
}

export default BetsAreaForSchedule;

